import { useState, useCallback } from 'react';
import type { ChatMessage } from '@state/chat';
import { bibleState } from '@/state/bible-state';
import { LoadingSpinner, LinkIcon, CheckIcon, SparklesIcon } from '@components/icons';
import { buildChatDeepLink } from '@utils/index';
import { ChatStatsFooter } from './chat-stats-footer';
import { ChatDraft } from './chat-draft';
import { AccuracyChecklist } from './accuracy-checklist';
import { VerificationReceipt } from './verification-receipt';
import { PipelineProgress } from './pipeline-progress';
import { InferenceNarrative } from './inference-narrative';
import { QueueStatusPanel } from './queue-status-panel';
import { RichMessageContent, ScriptureFirstMessage, PericopeStudyMessage, SearchResultsMessage, LocatorResultsMessage, ProgressiveChunks } from './rich-message';
import { MarkdownContent } from './markdown-content';

interface ChatMessageBubbleProps {
  message: ChatMessage;
}

export function ChatMessageBubble({ message }: ChatMessageBubbleProps) {
  const isUser = message.role === 'user';
  const [linkCopied, setLinkCopied] = useState(false);
  // Persisted accuracy checklist — folded by default, reopenable.
  const [checksOpen, setChecksOpen] = useState(false);

  const handleCopyLink = useCallback(async () => {
    const deepLink = buildChatDeepLink(message.content);
    try {
      await navigator.clipboard.writeText(deepLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      // Clipboard unavailable
    }
  }, [message.content]);

  if (message.isLoading) {
    const hasChunks = message.contentChunks && message.contentChunks.length > 0;
    const hasDraft = Boolean(message.streamingText || message.editBaseline);
    // The API's opener (pericope title / "It is written" quote) arrives as
    // one token before the model starts, so a draft on screen does not mean
    // the model is writing yet. Anything past that first token does.
    const isModelWriting = (message.streamingTokens ?? 0) > 1 || Boolean(message.editBaseline);
    return (
      <div className="chat-bubble chat-bubble-assistant" data-message-id={message.id}>
        {hasChunks && (
          <div className="mb-3">
            <ProgressiveChunks chunks={message.contentChunks!} />
          </div>
        )}
        {message.queueStatus
          ? <QueueStatusPanel status={message.queueStatus} />
          : message.progressSteps?.length
            ? <PipelineProgress
                steps={message.progressSteps}
                inferenceProgress={message.inferenceProgress}
                inferenceMeta={message.inferenceMeta}
                // While the opener sits on screen with an empty caret, the
                // wait is still on — the ticker just moves below the draft
                // (rendered under it, so it rides the bottom edge down).
                showNarrative={!hasDraft}
              />
            : <LoadingSpinner className="h-5 w-5 text-cyan-500" />
        }
        {hasDraft ? (
          // Live V6 draft + edit theater — unverified until the pipeline's
          // `complete` body replaces it (ground truth, never this buffer).
          <ChatDraft
            message={message}
            onTheaterDone={() => bibleState.markChatTheaterDone(message.id)}
          />
        ) : null}
        {hasDraft && !isModelWriting && (
          // Bottom of the feedback, under the opener and its caret. It goes
          // the moment the model's own words start — they are the better
          // thing to watch (Brian, UAT 2026-09-21).
          <InferenceNarrative
            active
            meta={message.inferenceMeta ? { ...message.inferenceMeta } : undefined}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`chat-bubble ${isUser ? 'chat-bubble-user' : 'chat-bubble-assistant'}`} data-message-id={message.id}>
      {/* The work panel keeps its place from the show: above the text. */}
      {!isUser && message.editChecks && message.editChecks.length > 0 && (
        <AccuracyChecklist
          title="Scripture accuracy checks"
          acts={message.editChecks.map((c) => ({
            act: c.act,
            label: c.label,
            status: c.status === 'skipped' ? ('skipped' as const) : ('done' as const),
          }))}
          collapsed={!checksOpen}
          onToggle={() => setChecksOpen(!checksOpen)}
        />
      )}
      {isUser ? (
        <div className="flex items-start gap-1">
          <p className="chat-bubble-text flex-1">{message.content}</p>
          <button
            onClick={handleCopyLink}
            className="mt-0.5 shrink-0 rounded p-1 text-stone-400 transition-colors hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300"
            title={linkCopied ? 'Link copied!' : 'Copy chat link'}
          >
            {linkCopied
              ? <CheckIcon className="h-3.5 w-3.5 text-green-300" />
              : <LinkIcon className="h-3.5 w-3.5" />
            }
          </button>
        </div>
      ) : message.searchResults ? (
        <SearchResultsMessage
          query={message.searchResults.query}
          bookName={message.searchResults.bookName}
          results={message.searchResults.results}
          total={message.searchResults.total}
        />
      ) : message.locatorResults ? (
        <LocatorResultsMessage
          query={message.locatorResults.query}
          entityName={message.locatorResults.entityName}
          results={message.locatorResults.results}
        />
      ) : message.recalledFrom ? (
        <div data-testid="recalled-commentary">
          <div className="mb-2 flex items-center gap-2 border-b border-stone-200 pb-2 text-sm text-stone-600 dark:border-stone-700 dark:text-stone-300">
            <SparklesIcon className="h-4 w-4 shrink-0 text-cyan-500" />
            <span className="min-w-0 truncate">
              It is written… —{' '}
              <em>{message.recalledFrom.pericopeTitle ?? message.recalledFrom.reference}</em>
              {' · '}
              {new Date(message.recalledFrom.generatedAt).toLocaleDateString()}
            </span>
          </div>
          <RichMessageContent
            content={message.content}
            commentary={message.commentary}
            rawModelBody={message.rawModelBody}
            prompt={message.prompt}
            reportMeta={message.reportMeta}
            readerThoughts={message.readerThoughts}
            itIsWritten={message.itIsWritten}
            reference={message.reference}
            pericope={message.pericope}
            passageVerses={message.passageVerses}
            originalQuery={message.originalQuery}
          />
        </div>
      ) : message.scriptureResponse?.scope === 'pericope' ? (
        <PericopeStudyMessage
          response={message.scriptureResponse}
          commentary={message.commentary}
          commentaryState={message.commentaryState ?? 'not-requested'}
          onRequestCommentary={(readerThoughts) =>
            bibleState.requestChatCommentary(message.id, readerThoughts)}
          initialThoughts={
            bibleState.editingStudy?.readerThoughts ?? bibleState.selectedStudy?.readerThoughts
          }
          progressSteps={message.progressSteps}
          inferenceProgress={message.inferenceProgress}
          inferenceMeta={message.inferenceMeta}
        />
      ) : message.scriptureResponse ? (
        <ScriptureFirstMessage
          response={message.scriptureResponse}
          commentary={message.commentary}
          rawModelBody={message.rawModelBody}
          commentaryState={message.commentaryState ?? 'not-requested'}
          onRequestCommentary={() => bibleState.requestChatCommentary(message.id)}
          originalQuery={message.originalQuery}
          reportMeta={message.reportMeta}
          readerThoughts={message.readerThoughts}
          progressSteps={message.progressSteps}
          inferenceProgress={message.inferenceProgress}
          inferenceMeta={message.inferenceMeta}
        />
      ) : message.isMarkdown ? (
        <MarkdownContent content={message.content} />
      ) : (
        <RichMessageContent
          content={message.content}
          metadata={message.metadata}
          commentary={message.commentary}
          rawModelBody={message.rawModelBody}
          prompt={message.prompt}
          reportMeta={message.reportMeta}
          readerThoughts={message.readerThoughts}
          contentChunks={message.contentChunks}
          originalQuery={message.originalQuery}
          itIsWritten={message.itIsWritten}
          reference={message.reference}
          strongsAnalysis={message.strongsAnalysis}
          textualVariant={message.textualVariant}
          crossReferences={message.crossReferences}
          crossRefTexts={message.crossRefTexts}
          pericope={message.pericope}
          variantSummary={message.variantSummary}
          passageVerses={message.passageVerses}
          // Standalone commentary follow-up to a scripture-first message —
          // the scripture-first bubble above already rendered scripture /
          // pericope / cross-refs / variants. Show only the commentary
          // and report button to avoid structural duplication.
          commentaryOnly={Boolean(message.commentary) && !message.scriptureResponse}
          // When a study card above owns the toolbar, the trailing Report
          // button here would be the same action twice.
          showReport={!message.commentaryForMessageId}
        />
      )}
      {message.isError && (
        <p className="chat-bubble-error">
          {message.errorDetail ?? 'Could not connect to server.'}
        </p>
      )}
      {message.isError && message.commentaryForMessageId && (
        <button
          type="button"
          className="chat-retry-button"
          onClick={() => bibleState.retryChatCommentary(message.id)}
          data-testid="commentary-retry"
        >
          Try again
        </button>
      )}
      {!isUser && message.editReceipt && message.editReceipt.length > 0 && (
        <VerificationReceipt items={message.editReceipt} />
      )}
      {!isUser && message.stats && (
        <ChatStatsFooter stats={message.stats} intent={message.intent} />
      )}
    </div>
  );
}
